import React from "react";
import { Minus, Plus } from "lucide-react";
import "./styles.css";

export interface NumberInputProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  className?: string;
  containerClassName?: string;
  variant?: "default" | "compact";
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  description,
  className = "",
  containerClassName = "",
  variant = "default",
}) => {
  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      onChange(min);
      return;
    }
    const numValue = parseInt(inputValue, 10);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      onChange(clampedValue);
    }
  };

  return (
    <div className={`number-input-container ${containerClassName}`}>
      {label && <label className="number-input-label">{label}</label>}
      <div className={`number-input-wrapper number-input-wrapper--${variant} ${className}`}>
        <button
          type="button"
          className={`number-input-button number-input-button--decrement number-input-button--${variant}`}
          onClick={handleDecrement}
          disabled={value <= min}
          aria-label="Decrement"
        >
          <Minus size={16} />
        </button>
        <input
          type="number"
          className={`number-input number-input--${variant}`}
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
        />
        <button
          type="button"
          className={`number-input-button number-input-button--increment number-input-button--${variant}`}
          onClick={handleIncrement}
          disabled={value >= max}
          aria-label="Increment"
        >
          <Plus size={16} />
        </button>
      </div>
      {description && <p className="number-input-description">{description}</p>}
    </div>
  );
};

export default NumberInput;

